import { useEffect, useState } from "react";
import { Search, RotateCcw } from "lucide-react";
import CustomLoader from "./CustomLoader";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";
import { Link } from "react-router-dom";

const BetHistoryDashboard = () => {
  // Date Helpers
  const formatDateTime = (date) => {
    const pad = (num) => String(num).padStart(2, "0");
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    const ss = pad(date.getSeconds());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
  };

  const getTodayStart = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return formatDateTime(d);
  };

  const getTodayEnd = () => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return formatDateTime(d);
  };

  // State Management
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [betHistory, setBetHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search filter states
  const [startDate, setStartDate] = useState(getTodayStart());
  const [endDate, setEndDate] = useState(getTodayEnd());
  const [provider, setProvider] = useState("All");
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState("All");
  const [gmt, setGmt] = useState("Local");
  const [gameType, setGameType] = useState("All");
  const [betId, setBetId] = useState("");
  const [event, setEvent] = useState("");
  const [gameCode, setGameCode] = useState("");
  const [timeType, setTimeType] = useState("Bet Time");
  const [market, setMarket] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setError("No token found. Please log in.");
      setLoading(false);
      return;
    }
    fetchUserApiBetHistory();
  }, [page, limit]);

  const fetchUserApiBetHistory = async () => {
    setLoading(true);
    try {
      // Build filter params
      const params = {
        page,
        pageSize: limit,
        startDate: startDate ? startDate.replace("T", " ") : undefined,
        endDate: endDate ? endDate.replace("T", " ") : undefined,
        provider: provider !== "All" ? provider : undefined,
        username: username.trim() || undefined,
        betId: betId.trim() || undefined,
        gameCode: gameCode.trim() || undefined,
        gameType: gameType !== "All" ? gameType : undefined,
        excludeSports: true,
      };

      const response = await api.get("/getUserApiBetHistory", {
        params,
        headers: {
          Authorization: `${token}`,
        },
      });

      const { data, pagination } = response.data;

      setBetHistory(data || []);
      setTotalPages(pagination.totalPages || 1);
      setTotalRecords(pagination.totalRecords || 0);
    } catch (err) {
      console.error("Failed to fetch bet history:", err);
      setError("Failed to fetch bet history");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUserApiBetHistory();
  };

  const handleReset = () => {
    setStartDate(getTodayStart());
    setEndDate(getTodayEnd());
    setProvider("All");
    setUsername("");
    setStatus("All");
    setGmt("Local");
    setGameType("All");
    setBetId("");
    setEvent("");
    setGameCode("");
    setTimeType("Bet Time");
    setMarket("");
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculations for current page totals
  const pageBetTotal = betHistory.reduce((sum, item) => sum + Number(item.bet_amount || 0), 0);
  const pageWinLossTotal = betHistory.reduce((sum, item) => sum + (item.status === 'pending' ? 0 : Number(item.win_amount || 0) - Number(item.bet_amount || 0)), 0);

  return (
    <div className="space-y-6">
      {/* Title & Breadcrumb */}
      <div className="flex justify-between items-center border-b pb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Betting History</h1>
        </div>
        <div className="text-xs text-gray-500 font-semibold space-x-1">
          <Link to="/" className="text-blue-600 hover:underline">Home</Link>
          <span>/</span>
          <span>Betting History</span>
        </div>
      </div>

      {/* Filter Form Block */}
      <form onSubmit={handleSearch} className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">

          {/* Column 1 */}
          <div className="space-y-3.5">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">From Date</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">To Date</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="All">All</option>
                <option value="Settled">Settled</option>
              </select>
            </div>
            <div className="flex gap-2 pt-2 pl-24">
              <button
                type="submit"
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-bold text-xs flex items-center gap-1.5 transition shadow-sm uppercase"
              >
                <Search className="w-3.5 h-3.5" />
                Search
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded font-semibold text-xs flex items-center gap-1 transition border shadow-sm"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            </div>
          </div>

          {/* Column 2 */}
          <div className="space-y-3.5">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Provider</label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="All">All</option>
                <option value="TADA">TADA</option>
                <option value="JILI">JILI</option>
                <option value="MiniGame">MiniGame</option>
                <option value="PGSoft">PGSoft</option>
                <option value="JDB">JDB</option>
                <option value="PragmaticPlay">PragmaticPlay</option>
                <option value="CQ9">CQ9</option>
                <option value="AGGaming">AGGaming</option>
                <option value="IdealGaming">IdealGaming</option>
                <option value="BGaming">BGaming</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Username</label>
              <input
                type="text"
                placeholder="Enter Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">GMT</label>
              <select
                value={gmt}
                onChange={(e) => setGmt(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="Local">Local</option>
                <option value="GMT+0">GMT+0</option>
              </select>
            </div>
          </div>

          {/* Column 3 */}
          <div className="space-y-3.5">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Game Type</label>
              <select
                value={gameType}
                onChange={(e) => setGameType(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="All">All</option>
                <option value="slots">slots</option>
                <option value="live">live</option>
                <option value="fish">fish</option>
                <option value="table">table</option>
                <option value="card">card</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Bet ID</label>
              <input
                type="text"
                placeholder="Enter Bet ID"
                value={betId}
                onChange={(e) => setBetId(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Event</label>
              <input
                type="text"
                value={event}
                onChange={(e) => setEvent(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Column 4 */}
          <div className="space-y-3.5">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Game Code</label>
              <input
                type="text"
                placeholder="Enter Game Code"
                value={gameCode}
                onChange={(e) => setGameCode(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Time Type</label>
              <select
                value={timeType}
                onChange={(e) => setTimeType(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="Bet Time">Bet Time</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-gray-600 w-24 flex-shrink-0">Market</label>
              <input
                type="text"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
                className="flex-1 border rounded px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

        </div>
      </form>

      {/* Main Table Block */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">

        {/* Table Controls (Show X Entries) */}
        <div className="flex justify-between items-center px-5 py-4 border-b">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
            <span>Show</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              className="border rounded px-2 py-1 bg-white focus:outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={2000}>2000</option>
              <option value={5000}>5000</option>
              <option value={10000}>10000</option>

            </select>
            <span>entries</span>
          </div>
        </div>

        {/* Content Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-20">
              <CustomLoader />
            </div>
          ) : error ? (
            <p className="text-center py-10 text-red-600 font-semibold">{error}</p>
          ) : (
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-gray-50 border-b text-gray-600 uppercase font-semibold">
                <tr>
                  {[
                    "Game Type",
                    "Game",
                    "Username",
                    "Bet ID",
                    "Game Result",
                    "Points Before Bet",
                    "Bet Amount",
                    "Win/Loss",
                    "Bet+win/Loss",
                    "Points After Bet",
                    "Bet Time",
                    "Settle Time",
                    "Status"
                  ].map((h) => (
                    <th key={h} className="px-4 py-3.5 border-r last:border-r-0 font-bold select-none cursor-pointer hover:bg-gray-100 transition-colors">
                      <div className="flex items-center justify-between">
                        <span>{h}</span>
                        <span className="text-[10px] text-gray-400">⇅</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {betHistory.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="text-center py-10 font-semibold text-gray-400">
                      No matching records found
                    </td>
                  </tr>
                ) : (
                  betHistory.map((item) => {
                    const isPending = item.status === 'pending';
                    const winLoss = isPending ? 0 : Number(item.win_amount || 0) - Number(item.bet_amount || 0);
                    const betWinLoss = Number(item.bet_amount || 0) + winLoss;

                    let cb = item.callback_data;
                    if (typeof cb === 'string') {
                      try {
                        cb = JSON.parse(cb);
                      } catch (e) {
                        cb = null;
                      }
                    }

                    const pointsBefore = cb?.balance_before !== undefined && cb?.balance_before !== null
                      ? formatCurrency(cb.balance_before)
                      : "-";
                    const pointsAfter = cb?.balance_after !== undefined && cb?.balance_after !== null
                      ? formatCurrency(cb.balance_after)
                      : "-";

                    return (
                      <tr key={item.id} className="hover:bg-gray-50/50 transition">
                        <td className="px-4 py-3 border-r font-medium text-gray-600">{item.category_name || "-"}</td>
                        <td className="px-4 py-3 border-r font-semibold text-gray-800">{item.game_name || item.game_uid || "-"}</td>
                        <td className="px-4 py-3 border-r font-semibold text-gray-800">{item.username ? `(${item.id_user}) ${item.username}` : "-"}</td>
                        <td className="px-4 py-3 border-r text-gray-600">{item.id}</td>
                        <td className="px-4 py-3 border-r text-gray-600 font-medium">
                          {isPending ? (
                            <span className="text-amber-500 font-semibold">Pending</span>
                          ) : Number(item.win_amount) > 0 ? (
                            <span className="text-emerald-600">Win</span>
                          ) : (
                            <span className="text-rose-600">Loss</span>
                          )}
                        </td>
                        <td className="px-4 py-3 border-r font-medium text-gray-700">{pointsBefore}</td>
                        <td className="px-4 py-3 border-r font-semibold text-gray-900">{formatCurrency(item.bet_amount)}</td>
                        <td className={`px-4 py-3 border-r font-bold ${isPending ? "text-amber-500" : winLoss >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {isPending ? "Pending" : winLoss >= 0 ? `+${formatCurrency(winLoss)}` : formatCurrency(winLoss)}
                        </td>
                        <td className="px-4 py-3 border-r font-semibold text-gray-900">{formatCurrency(betWinLoss)}</td>
                        <td className="px-4 py-3 border-r font-medium text-gray-700">{pointsAfter}</td>
                        <td className="px-4 py-3 border-r text-gray-600">{new Date(item.bet_timestamp).toLocaleString()}</td>
                        <td className="px-4 py-3 border-r text-gray-600">{new Date(item.bet_timestamp).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          {isPending ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              pending
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              settled
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {/* Footer Summary Row */}
              <tfoot className="bg-gray-50 border-t font-bold text-gray-800">
                <tr>
                  <td colSpan={5} className="px-4 py-3.5 text-right border-r">Total:</td>
                  <td className="px-4 py-3.5 border-r"></td>
                  <td className="px-4 py-3.5 border-r font-extrabold">{formatCurrency(pageBetTotal)}</td>
                  <td className={`px-4 py-3.5 border-r font-extrabold ${pageWinLossTotal >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {pageWinLossTotal >= 0 ? `+${formatCurrency(pageWinLossTotal)}` : formatCurrency(pageWinLossTotal)}
                  </td>
                  <td className="px-4 py-3.5 border-r font-extrabold">{formatCurrency(pageBetTotal + pageWinLossTotal)}</td>
                  <td className="px-4 py-3.5 border-r"></td>
                  <td className="px-4 py-3.5 border-r"></td>
                  <td className="px-4 py-3.5 border-r"></td>
                  <td className="px-4 py-3.5"></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Table Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-5 py-4 border-t bg-gray-50/50">
            <span className="text-xs text-gray-500 font-medium">
              Showing {betHistory.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
                className="px-3 py-1.5 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-xs font-semibold transition"
              >
                Previous
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="px-3 py-1.5 border rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-xs font-semibold transition"
              >
                Next
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default BetHistoryDashboard;
